import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import nsPortal from '@netsapiens/netsapiens-js/dist/portal';
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { IntlProvider, addLocaleData } from 'react-intl';

import { MuiThemeProvider, createTheme } from '@material-ui/core/styles';

import * as actions from '../state';
import {
  ServiceNotSupportedMsg,
  BrowserSupportMsg,
  Loader,
  UCLicenseMsg,
  ForgotLogin,
  ForgotPassword,
  LoginForm,
  PasswordReset,
  SubscriberSelect,
  SsoEnroll,
  MfaForm,
  Dashboard,
  NoBoard,
  InvalidBoard,
} from './pages';
import Wrapper from './layouts/wrapper/Wrapper';
import appStyles from '../styles.module.css';

// typography v2 migration
window.__MUI_USE_NEXT_TYPOGRAPHY_VARIANTS__ = true;

function App({
  appURL,
}) {
  const dispatch = useDispatch();

  const layout = useSelector(actions.selectLayout);
  const board = useSelector(actions.readStateBoard);
  const user = useSelector(actions.readStateUser);
  // eslint-disable-next-line max-len
  const brandPrimaryColorByHost = useSelector((state) => actions.selectConfig(state, `PORTAL_CSS_PRIMARY_1_${window.location.hostname}`));
  const brandPrimaryColor = useSelector((state) => actions.selectConfig(state, 'PORTAL_CSS_PRIMARY_1'));
  // eslint-disable-next-line max-len
  const brandSecondaryColorByHost = useSelector((state) => actions.selectConfig(state, `PORTAL_CSS_SECONDARY_1_${window.location.hostname}`));
  const brandSecondaryColor = useSelector((state) => actions.selectConfig(state, 'PORTAL_CSS_SECONDARY_1'));

  const [translations, setTranslations] = useState(null);
  const [usersLocale, setUsersLocale] = useState(localStorage.getItem('chartsRenderedLang'));
  const [usersLang, setUsersLang] = useState(localStorage.getItem('chartsRenderedLang')?.split('_')[0]);

  useEffect(() => {
    if (localStorage.getItem('chartsRenderedLang') == null) {
      localStorage.setItem('chartsRenderedLang', navigator.language.toLowerCase().replace('-', '_') || 'en_us');
    }

    setUsersLocale(localStorage.getItem('chartsRenderedLang'));
    setUsersLang(localStorage.getItem('chartsRenderedLang')?.split('_')[0]);
  }, []);

  useEffect(() => {
    if (usersLocale && usersLocale !== 'en_us') {
      nsPortal.getTranslation(usersLocale)
        .then((res) => {
          if (typeof res === 'object') {
            setTranslations(res);
            addLocaleData({ locale: usersLang, pluralRuleFunction: () => {}, ...res });
          } else {
            console.error(
              `getTranslation('${usersLocale}') request did not return an appropriate localized dataset.`,
            );
          }
        }).catch((err) => {
          console.error(err);
        });
    }
  }, [usersLocale]);

  useEffect(() => {
    if (user != null && user.language !== localStorage.getItem('chartsRenderedLang')) {
      localStorage.setItem('chartsRenderedLang', user.language);
      window.location = appURL;
    }
  }, [user?.language]);

  useEffect(() => {
    // if there is a branded color and it hasn't been loaded into the layout state, do it now
    const primaryColor = brandPrimaryColorByHost || brandPrimaryColor;
    if (primaryColor
      && primaryColor !== layout.brandPrimaryColor
    ) {
      dispatch(actions.setBrandPrimaryColor(primaryColor));
    }

    const secondaryColor = brandSecondaryColorByHost || brandSecondaryColor;
    if (secondaryColor
      && secondaryColor !== layout.brandSecondaryColor
    ) {
      dispatch(actions.setBrandSecondaryColor(secondaryColor));
    }
  }, [brandPrimaryColorByHost, brandPrimaryColor]);

  const browserSupportMessage = () => (layout.showBrowserSupportPage && <BrowserSupportMsg />) || null;

  const loader = () => (layout.showLoaderPage && <Loader />) || null;

  const ucLicenseMsg = () => (layout.showUCLicensePage && <UCLicenseMsg message="UCLicenseMsg" />) || null;

  const noServiceMsg = () => (layout.showNoServicePage && <ServiceNotSupportedMsg />) || null;

  const passwordReset = () => (layout.showPasswordResetPage && <PasswordReset />) || null;

  const loginForm = () => (layout.showLoginFormPage && <LoginForm />) || null;

  const subscriberSelect = () => (layout.showSubscriberSelectPage
      && <SubscriberSelect {...layout.showSubscriberSelectPage} /> // showSubscriberSelectPage contains data for the sso id
  ) || null;

  const ssoEnroll = () => (layout.showSsoEnrollPage
      && <SsoEnroll {...layout.showSsoEnrollPage} /> // showSsoEnrollPage contains data for the sso id
  ) || null;

  const mfaForm = () => (layout.showMfaFormPage
      && <MfaForm {...layout.showMfaFormPage} /> // showMfaFormPage contains data for the mfa process
  ) || null;

  const forgotLogin = () => (layout.showForgotLoginPage && <ForgotLogin />) || null;

  const forgotPassword = () => (layout.showForgotPasswordPage && <ForgotPassword />) || null;

  const WrapperComponent = () => {
    let content;
    if (layout.isLoading === false) {
      if (board && board.error) {
        content = <InvalidBoard />;
      } else if (!board || !board.id) {
        content = <NoBoard />;
      } else {
        content = <Dashboard />;
      }
    }
    return (layout.showWrapperPage && <Wrapper content={content} />) || null;
  };

  const brandTheme = {
    palette: {
      type: 'light',
      primary: {
        main: layout.brandPrimaryColor,
      },
      secondary: {
        main: layout.brandSecondaryColor,
      },
    },
    overrides: {
      MuiAppBar: {
        colorPrimary: {
          backgroundColor: layout.brandPrimaryColor,
        },
      },
      MuiLink: {
        root: {
          color: layout.brandPrimaryColor || 'rgb(0, 114, 188) !important',
          cursor: 'pointer',
        },
      },
      MuiSwitch: {
        colorSecondary: {
          color: layout.brandSecondaryColor,
        },
        iconChecked: {
          color: layout.brandSecondaryColor,
        },
      },
      MuiSlider: {
        track: {
          backgroundColor: layout.brandSecondaryColor,
        },
        thumb: {
          backgroundColor: layout.brandSecondaryColor,
        },
      },
    },
  };

  return (
    <IntlProvider locale={usersLang} messages={translations}>
      <MuiPickersUtilsProvider utils={MomentUtils}>
        <MuiThemeProvider theme={createTheme(brandTheme)}>
          <div
            className={appStyles.fullHeight}
            style={{ overflowY: layout.showSubscriberSelectPage ? 'scroll' : 'hidden' }}
          >
            {/* todo turn into routes after react-redux-router is ready */}
            { browserSupportMessage() }
            { noServiceMsg() }
            { loader() }
            { ucLicenseMsg() }
            { subscriberSelect() }
            { ssoEnroll() }
            { mfaForm() }
            { forgotLogin() }
            { forgotPassword() }
            { passwordReset() }
            { loginForm() }
            { WrapperComponent() }
          </div>
        </MuiThemeProvider>
      </MuiPickersUtilsProvider>
    </IntlProvider>
  );
}

export default App;
